<!--
  최상위 컴포넌트. 라우터가 그린 화면을 그대로 보여 준다.
  레이아웃(사이드바·헤더)은 라우트 쪽에서 감싼다 — 로그인 화면은 레이아웃이 없어야 한다.

  여기서 하는 일이 둘 더 있다 (2026-10-20, DB #89).

  ① **로그인이 끊기면 팝업으로 알린다.** 전에는 조용히 로그인 화면으로 보냈는데,
     그러면 쓰던 화면이 왜 사라졌는지 알 수 없다. 끊긴 까닭(만료·비밀번호 변경·계정 중지)을
     그대로 보여 주고, 확인을 누르면 로그인으로 보낸다.
  ② **보고만 있어도 끊기지 않게** 심장박동을 켠다. 토큰 수명은 요청을 보낼 때마다
     늘어나는데, 글을 길게 쓰거나 지도를 보고만 있으면 요청이 안 나간다.
     `startHeartbeat()` 가 사람이 움직였고 탭이 보일 때만 가벼운 신호를 보낸다.
-->
<template>
  <router-view />
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted } from 'vue'
import { ElMessageBox } from 'element-plus'
import { useRouter } from 'vue-router'
import { onSessionLost, startHeartbeat, stopHeartbeat } from '@/utils/auth'

const router = useRouter()

onMounted(() => {
  onSessionLost(async (reason, message) => {
    // 이미 로그인 화면이면 띄우지 않는다 — 거기서는 끊긴 것이 당연하다
    if (router.currentRoute.value.name === 'login') return

    // 하던 자리를 기억해 둔다 — 단 **비밀번호 바꾸기는 기억하지 않는다**.
    // 들렀다 지나가는 화면이라, 기억하면 다시 로그인할 때 그리로 끌려간다(2026-10-20)
    const at = router.currentRoute.value.fullPath
    const where = at.startsWith('/my-password') ? '/dashboard' : at
    try {
      await ElMessageBox.alert(detail(reason), message, {
        confirmButtonText: '다시 로그인',
        type: 'warning',
        // 바깥을 눌러 닫고 그 화면에 남아 있으면 요청마다 401 이 난다
        closeOnClickModal: false,
        closeOnPressEscape: false,
      })
    } catch {
      /* 어떻게 닫혔든 로그인으로 보낸다 */
    }
    router.push({ name: 'login', query: { redirect: where } })
  })

  startHeartbeat()
})

onBeforeUnmount(stopHeartbeat)

/** 끊긴 까닭을 사람 말로 — 서버가 준 `reason` 을 쓴다 */
function detail(reason: string) {
  switch (reason) {
    case 'password_changed':
      return '비밀번호가 바뀌었습니다. 다른 기기에서 바꾸면 이 기기는 로그아웃됩니다.'
    case 'no_token':
      return '로그인 정보가 없습니다.'
    default:
      // 만료 · 계정 중지 · 토큰이 깨진 경우가 여기로 온다
      return '로그인 유효 시간이 지났거나 계정이 중지되었습니다. 다시 로그인해 주세요.'
  }
}
</script>
